import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'semantic-ui-react';
import './styles.scss';
import { getInnerURL } from 'app/utils/languageUtil';
import { selectRoute } from 'app/redux/route/reducer';
import { selectLocale } from 'app/redux/language/selectors';
import { userSetLanguage } from 'app/redux/language/actions';

interface LanguageOption {
  key: string;
  text: string;
  locale: string;
  flag: string;
}

type LocationType = 'accountPage' | 'navbarBurger' | undefined;

interface LanguageDropdownProps {
  location?: LocationType;
}

const languageOptions: LanguageOption[] = [
  { key: 'Français', text: 'Français', locale: 'fr', flag: 'fr' },
  { key: 'English', text: 'English', locale: 'en', flag: 'uk' },
  { key: 'Deutsch', text: 'Deutsch', locale: 'de', flag: 'de' },
  { key: 'Serbian', text: 'Srpski', locale: 'sr', flag: 'rs' },
  { key: 'Greek', text: 'Ελληνικά', locale: 'el', flag: 'gr' },
  { key: 'Netherlands', text: 'Nederlands', locale: 'nl', flag: 'nl' },
  { key: 'Norwegian', text: 'Norsk', locale: 'nb', flag: 'no' },
  { key: 'Russian', text: 'Русский', locale: 'ru', flag: 'ru' },
  { key: 'Ukrainian', text: 'Українська', locale: 'uk', flag: 'ua' },
  { key: 'Italian', text: 'Italiano', locale: 'it', flag: 'it' },
  { key: 'Finnish', text: 'Suomea', locale: 'fi', flag: 'fi' },
  { key: 'Portuguese', text: 'Português', locale: 'pt', flag: 'pt' },
  { key: 'Slovak', text: 'Slovenský', locale: 'sk', flag: 'sk' },
  { key: 'Spanish', text: 'Español', locale: 'es', flag: 'es' },
  { key: 'Czech', text: 'Čeština', locale: 'cz', flag: 'cz' },
  { key: 'Korean', text: '한국어', locale: 'ko', flag: 'kr' },
  { key: 'Swedish', text: 'Sverige', locale: 'sv', flag: 'se' },
];

const LanguageMenu: React.FC = () => {
  const dispatch = useDispatch();
  const currentPathname = useSelector(selectRoute).locationBeforeTransitions.pathname;
  const innerURL = getInnerURL(currentPathname);

  const handleLanguageLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    // @ts-ignore
    const locale = event.currentTarget.dataset.locale;
    if (locale) {
      dispatch(userSetLanguage(locale));
    }
  };

  return languageOptions.map((languageOption) => (
    <li className="language-dropdown__item" key={languageOption.text}>
      <a
        href={`/${languageOption.locale}/${innerURL}`}
        className="language-dropdown__link"
        onClick={handleLanguageLinkClick}
        data-locale={languageOption.locale}
      >
        <i className={`${languageOption.flag} flag`} />
        {languageOption.text}
      </a>
    </li>
  ));
};

const CurrentLanguageLabel: React.FC = () => {
  const currentLocale = useSelector(selectLocale);
  const currentLanguageOption = languageOptions.find(
    (languageOption) => languageOption.locale === currentLocale
  );

  if (!currentLanguageOption) {
    return null;
  }

  return (
    <>
      <i className={`${currentLanguageOption.flag} flag`} />
      {currentLanguageOption.text}
    </>
  );
};

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({ location }) => {
  let dropdownClassName = 'link item'; // Default navbarFullSize location style
  if (location === 'accountPage') {
    dropdownClassName = 'accountPage';
  }
  if (location === 'navbarBurger') {
    dropdownClassName = 'navbarBurger';
  }

  return (
    <Dropdown trigger={<CurrentLanguageLabel />} className={dropdownClassName}>
      <Dropdown.Menu as="ul" className="language-dropdown">
        <LanguageMenu />
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default LanguageDropdown;
